<template>
  <div class="course-class-homework-upload">
    <h2>课程作业批量上传</h2>

    <!-- 加载状态 -->
    <div v-if="!homeworkInfo" class="loading-section">
      <div class="loading-content">
        <i class="el-icon-loading"></i>
        <p>正在加载作业信息...</p>
        <el-button type="text" size="mini" @click="forceShowContent" style="margin-top: 10px;">
          如果加载时间过长，点击这里强制显示
        </el-button>
      </div>
    </div>

    <!-- 作业信息 -->
    <div class="info-section" v-if="homeworkInfo && homeworkInfo.id">
      <div class="homework-info">
        <div class="homework-header">
          <i class="el-icon-document"></i>
          <h3>作业信息</h3>
        </div>
        <el-card class="homework-card" shadow="hover">
          <div class="homework-content">
            <div class="homework-name">
              <span class="label">作业名称：</span>
              <span class="value">{{ homeworkInfo.name }}</span>
            </div>
            <div class="homework-id">
              <span class="label">作业ID：</span>
              <el-tag type="info" size="small">{{ homeworkInfo.id }}</el-tag>
            </div>
          </div>
        </el-card>
      </div>
    </div>

    <el-divider v-if="homeworkInfo" content-position="center">学生作业批量上传</el-divider>

    <!-- 批量上传区域 -->
    <div v-if="homeworkInfo" class="batch-upload-container">
      <div class="batch-upload-section">
        <div class="batch-info">
          <el-alert
            title="批量上传说明"
            type="info"
            description="支持批量上传多个图片文件，系统将自动识别学生信息并批改作业"
            show-icon
            :closable="false">
          </el-alert>
        </div>

        <div class="batch-image-upload">
          <h4>批量上传图片</h4>
          <el-upload
            class="upload-demo"
            drag
            action="#"
            :auto-upload="false"
            :on-change="handleBatchFileChange"
            :on-remove="handleBatchFileRemove"
            :file-list="batchFileList"
            multiple
            :limit="20">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip" slot="tip">只能上传jpg/png文件，最多20张</div>
          </el-upload>
        </div>

        <div class="upload-actions">
          <el-button type="primary" @click="submitBatchUpload" :disabled="!canSubmitBatch" :loading="uploading">批量上传</el-button>
          <el-button @click="resetBatchUpload">重置</el-button>
        </div>
      </div>
    </div>

    <div class="upload-history" v-if="homeworkInfo && uploadHistory.length > 0">
      <el-divider content-position="center">上传历史</el-divider>
      <el-table :data="uploadHistory" border style="width: 100%">
        <el-table-column prop="studentName" label="学生姓名" width="120"></el-table-column>
        <el-table-column prop="fileName" label="文件名" width="150"></el-table-column>
        <el-table-column prop="uploadTime" label="上传时间" width="150"></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'">
              {{ scope.row.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="批改结果" width="200">
          <template slot-scope="scope">
            <div v-if="scope.row.judgeResult">
              <el-button type="text" @click="viewJudgeResult(scope.row)">查看结果</el-button>
            </div>
            <div v-else-if="scope.row.errorMessage">
              <el-tooltip :content="scope.row.errorMessage" placement="top">
                <span style="color: #f56c6c;">{{ scope.row.errorMessage }}</span>
              </el-tooltip>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120">
          <template slot-scope="scope">
            <el-button type="text" @click="previewImage(scope.row)">预览</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog title="图片预览" :visible.sync="previewDialogVisible" width="50%">
      <div class="image-preview-container">
        <img v-if="previewImageUrl" :src="previewImageUrl" class="preview-image" alt="预览图片">
        <div v-else class="no-image">暂无预览图片</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="previewDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 批改结果对话框 -->
    <el-dialog title="批改结果" :visible.sync="judgeResultDialogVisible" width="90%" :close-on-click-modal="false">
      <div class="judge-result-container" v-if="currentJudgeResult">
        <!-- 处理judgeQuestion原始响应格式 -->
        <div v-if="currentJudgeResult.result && currentJudgeResult.result.length > 0">
          <div v-for="(studentResult, index) in currentJudgeResult.result" :key="index" class="student-result">
            <!-- 学生信息和总体状态 -->
            <div class="student-header">
              <div class="student-info">
                <h3>学生：{{ studentResult.student_name }} ({{ studentResult.student_num }})</h3>
              </div>
              <div class="overall-status">
                <el-tag :type="getOverallStatusType(studentResult.results)" size="large">
                  {{ getOverallStatusText(studentResult.results) }}
                </el-tag>
              </div>
            </div>

            <!-- 统计信息 -->
            <div class="statistics" v-if="studentResult.results && studentResult.results.length > 0">
              <el-row :gutter="20">
                <el-col :span="6">
                  <div class="stat-item">
                    <div class="stat-number">{{ studentResult.results.length }}</div>
                    <div class="stat-label">总题数</div>
                  </div>
                </el-col>
                <el-col :span="6">
                  <div class="stat-item correct">
                    <div class="stat-number">{{ getCorrectCount(studentResult.results) }}</div>
                    <div class="stat-label">正确</div>
                  </div>
                </el-col>
                <el-col :span="6">
                  <div class="stat-item error">
                    <div class="stat-number">{{ getErrorCount(studentResult.results) }}</div>
                    <div class="stat-label">错误</div>
                  </div>
                </el-col>
                <el-col :span="6">
                  <div class="stat-item">
                    <div class="stat-number">{{ getAccuracy(studentResult.results) }}%</div>
                    <div class="stat-label">正确率</div>
                  </div>
                </el-col>
              </el-row>
            </div>

            <!-- 题目详情表格 -->
            <div class="questions-table" v-if="studentResult.results && studentResult.results.length > 0">
              <el-table :data="getSortedResults(studentResult.results)" border style="width: 100%; margin-top: 20px;" :row-class-name="getRowClassName">
                <el-table-column prop="matched_index" label="题号" width="80" align="center">
                  <template slot-scope="scope">
                    <el-tag size="small" type="info">{{ scope.row.matched_index }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="stem" label="题目" min-width="300">
                  <template slot-scope="scope">
                    <div class="question-content" v-html="renderLatex(scope.row.stem)"></div>
                  </template>
                </el-table-column>
                <el-table-column prop="student_answer" label="学生答案" width="200">
                  <template slot-scope="scope">
                    <div class="answer-content" v-html="renderLatex(scope.row.student_answer)"></div>
                  </template>
                </el-table-column>
                <el-table-column prop="judge" label="判断结果" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.judge === 'right' ? 'success' : 'danger'" size="medium">
                      {{ scope.row.judge === 'right' ? '正确' : '错误' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="answer" label="标准答案" width="200">
                  <template slot-scope="scope">
                    <div class="answer-content" v-html="renderLatex(scope.row.answer)"></div>
                  </template>
                </el-table-column>
                <el-table-column prop="comment" label="评语" min-width="250">
                  <template slot-scope="scope">
                    <div class="comment-content" v-if="scope.row.comment">
                      {{ scope.row.comment }}
                    </div>
                    <div class="comment-content" v-else style="color: #999;">-</div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
        <!-- 处理旧格式（recognizedQuestions） -->
        <div v-else-if="currentJudgeResult.recognizedQuestions && currentJudgeResult.recognizedQuestions.length > 0">
          <div class="student-result">
            <!-- 学生信息和总体状态 -->
            <div class="student-header">
              <div class="student-info">
                <h3>学生：批量上传结果</h3>
              </div>
              <div class="overall-status">
                <el-tag :type="getOverallStatusType(currentJudgeResult.recognizedQuestions)" size="large">
                  {{ getOverallStatusText(currentJudgeResult.recognizedQuestions) }}
                </el-tag>
              </div>
            </div>

            <!-- 统计信息 -->
            <div class="statistics">
              <el-row :gutter="20">
                <el-col :span="6">
                  <div class="stat-item">
                    <div class="stat-number">{{ currentJudgeResult.recognizedQuestions.length + (currentJudgeResult.unrecognizedQuestions ? currentJudgeResult.unrecognizedQuestions.length : 0) }}</div>
                    <div class="stat-label">总题数</div>
                  </div>
                </el-col>
                <el-col :span="6">
                  <div class="stat-item correct">
                    <div class="stat-number">{{ getCorrectCount(currentJudgeResult.recognizedQuestions) }}</div>
                    <div class="stat-label">正确</div>
                  </div>
                </el-col>
                <el-col :span="6">
                  <div class="stat-item error">
                    <div class="stat-number">{{ getErrorCount(currentJudgeResult.recognizedQuestions) }}</div>
                    <div class="stat-label">错误</div>
                  </div>
                </el-col>
                <el-col :span="6">
                  <div class="stat-item">
                    <div class="stat-number">{{ getAccuracy(currentJudgeResult.recognizedQuestions) }}%</div>
                    <div class="stat-label">正确率</div>
                  </div>
                </el-col>
              </el-row>
            </div>

            <!-- 题目详情表格 -->
            <div class="questions-table">
              <el-table :data="getSortedResults(currentJudgeResult.recognizedQuestions)" border style="width: 100%; margin-top: 20px;" :row-class-name="getRowClassName">
                <el-table-column prop="matched_index" label="题号" width="80" align="center">
                  <template slot-scope="scope">
                    <el-tag size="small" type="info">{{ scope.row.matched_index || (scope.$index + 1) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="content" label="题目" min-width="300">
                  <template slot-scope="scope">
                    <div class="question-content" v-html="renderLatex(scope.row.content || scope.row.stem)"></div>
                  </template>
                </el-table-column>
                <el-table-column prop="studentAnswer" label="学生答案" width="200">
                  <template slot-scope="scope">
                    <div class="answer-content" v-html="renderLatex(scope.row.studentAnswer)"></div>
                  </template>
                </el-table-column>
                <el-table-column prop="judge" label="判断结果" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.judge === 'right' ? 'success' : 'danger'" size="medium">
                      {{ scope.row.judge === 'right' ? '正确' : '错误' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="answer" label="标准答案" width="200">
                  <template slot-scope="scope">
                    <div class="answer-content" v-html="renderLatex(scope.row.answer || scope.row.correctAnswer)"></div>
                  </template>
                </el-table-column>
                <el-table-column prop="comment" label="评语" min-width="250">
                  <template slot-scope="scope">
                    <div class="comment-content" v-if="scope.row.comment">
                      {{ scope.row.comment }}
                    </div>
                    <div class="comment-content" v-else style="color: #999;">-</div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
        <div v-else>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="批改状态">
              <el-tag type="danger">失败</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="批改详情" :span="2">
              <pre style="white-space: pre-wrap; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(currentJudgeResult, null, 2) }}</pre>
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </div>
      <div v-else class="no-result">
        暂无批改结果
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="judgeResultDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { addStudentSubmission, uploadFile, judgeQuestion, saveCorrectionResult, updateSubmissionStatus, addWrongQuestion, getSpecificHomeworkQuestions } from '@/api/homework';
import { getToken } from '@/utils/auth';
import { parseJwt } from '@/utils/jwtUtils';

export default {
  name: 'CourseClassHomeworkUpload',
  props: {
    homework: {
      type: Object,
      required: false,
      default: () => null
    },
    courseId: {
      type: [String, Number],
      required: false,
      default: null
    }
  },
  data() {
    return {
      batchFileList: [],
      uploadHistory: [],
      uploading: false,
      previewDialogVisible: false,
      previewImageUrl: '',

      // 批改结果对话框相关数据
      judgeResultDialogVisible: false,
      currentJudgeResult: null,

      // 作业信息
      homeworkId: null,
      homeworkInfo: null,
      currentHomework: null // 当前作业对象，包含题目信息
    }
  },
  computed: {
    canSubmitBatch() {
      return this.batchFileList.length > 0;
    }
  },
  async created() {
    console.log('CourseClassHomeworkUpload created');
    console.log('Homework prop:', this.homework);
    console.log('Course ID prop:', this.courseId);
    await this.initHomeworkInfo();

    // 添加组件状态调试信息
    this.$nextTick(() => {
      console.log('组件初始化完成后的状态:');
      console.log('homeworkInfo:', this.homeworkInfo);
      console.log('homeworkId:', this.homeworkId);
      console.log('canSubmitBatch:', this.canSubmitBatch);
    });
  },
  watch: {
    // 监听 homework prop 的变化
    homework: {
      handler(newVal, oldVal) {
        console.log('homework prop 发生变化:', { newVal, oldVal });
        if (newVal && newVal.id) {
          this.initHomeworkInfo();
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // 初始化作业信息
    async initHomeworkInfo() {
      console.log('开始初始化作业信息，homework prop:', this.homework);

      // 如果有props传入的homework对象，使用它
      if (this.homework && this.homework.id) {
        this.homeworkInfo = this.homework;
        this.currentHomework = this.homework;
        this.homeworkId = this.homework.id;

        console.log('作业ID:', this.homeworkId, '类型:', typeof this.homeworkId);
        console.log('作业信息:', this.homeworkInfo);
      } else {
        console.warn('未提供作业信息，使用默认作业信息');
        // 使用默认作业信息，确保组件可以正常显示
        this.homeworkInfo = {
          id: 7,
          name: '作业1'
        };
        this.currentHomework = this.homeworkInfo;
        this.homeworkId = 7;
      }

      // 获取作业的题目列表和答案
      await this.fetchHomeworkQuestions();
    },

    // 获取作业的题目列表和答案
    async fetchHomeworkQuestions() {
      try {
        // 1. 获取特定作业的题目列表
        const homeworkQuestionsResponse = await getSpecificHomeworkQuestions(this.homeworkId);

        if (homeworkQuestionsResponse && homeworkQuestionsResponse.data) {
          const questions = homeworkQuestionsResponse.data;

          // 直接使用getSpecificHomeworkQuestions返回的题目数据，已经包含答案
          const questionsWithDetails = questions.map(question => ({
            content: question.content || question.stem || '',
            stem: question.content || question.stem || '',
            answer: question.answer || '',
            knowledge: question.knowledge || '',
            analysis: question.analysis || ''
          }));

          // 更新 currentHomework 对象
          this.currentHomework = {
            id: this.homeworkId,
            name: this.homeworkInfo.name,
            questions: questionsWithDetails
          };

          console.log('获取到的作业题目和答案:', this.currentHomework);
        } else {
          console.warn('未获取到作业题目列表');
          // 如果没有获取到题目，初始化为空数组
          this.currentHomework = {
            id: this.homeworkId,
            name: this.homeworkInfo.name,
            questions: []
          };
        }
      } catch (error) {
        console.error('获取作业题目失败:', error);
        // 如果获取失败，初始化为空数组
        this.currentHomework = {
          id: this.homeworkId,
          name: this.homeworkInfo.name,
          questions: []
        };
      }
    },

    // 调试组件状态
    debugComponentState() {
      console.log('=== 组件状态调试 ===');
      console.log('homeworkInfo:', this.homeworkInfo);
      console.log('homeworkId:', this.homeworkId);
      console.log('batchFileList:', this.batchFileList);
      console.log('canSubmitBatch:', this.canSubmitBatch);
      console.log('uploading:', this.uploading);
      console.log('==================');

      this.$message.info('组件状态已输出到控制台');
    },

    // 强制显示内容
    forceShowContent() {
      console.log('强制显示内容');
      this.homeworkInfo = {
        id: 7,
        name: '作业1'
      };
      this.currentHomework = this.homeworkInfo;
      this.homeworkId = 7;
      this.$message.success('已强制显示内容，请尝试操作');
    },

    // 处理批量文件变化
    handleBatchFileChange(file, fileList) {
      this.batchFileList = fileList;
    },

    // 处理批量文件移除
    handleBatchFileRemove(file, fileList) {
      this.batchFileList = fileList;
    },

    // 提交批量上传
    async submitBatchUpload() {
      if (!this.canSubmitBatch) {
        this.$message.warning('请至少上传一个文件');
        return;
      }

      this.uploading = true;
      try {
        // 获取所有文件
        const files = this.batchFileList.map(file => file.raw);

        // 收集所有批改结果
        const allCorrectionResults = [];

        // 逐个处理文件
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          try {
            // 显示当前正在处理的图片信息
            this.$message({
              message: `正在上传并批改第 ${i + 1} 张图片...`,
              type: 'info',
              duration: 1500
            });

            // 1. 上传当前的图片到 MinIO
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            const minioResponse = await uploadFile(uploadFormData);

            // 检查上传结果
            if (minioResponse.code !== 1) {
              console.error(`图片 ${i + 1} 上传失败:`, minioResponse.msg || '未知错误');
              this.uploadHistory.unshift({
                studentName: '未知学生',
                fileName: file.name,
                uploadTime: new Date().toLocaleString(),
                status: 'error',
                imageUrl: URL.createObjectURL(file),
                errorMessage: minioResponse.msg || '文件上传失败'
              });
              continue; // 当前图片处理失败，跳过后续步骤，继续处理下一张
            }

            const imageUrl = `http://localhost:8080/api/files${minioResponse.msg}`;

            // 2. 调用 judgeQuestion 接口对当前的图片进行批改
            const currentHomeworkQuestions = this.currentHomework.questions || [];

            // 添加调试信息
            console.log('=== 批量上传 judgeQuestion 调试信息 ===');
            console.log('currentHomework:', this.currentHomework);
            console.log('currentHomeworkQuestions:', currentHomeworkQuestions);
            console.log('currentHomeworkQuestions.length:', currentHomeworkQuestions.length);

            // 检查是否有题目数据
            if (currentHomeworkQuestions.length === 0) {
              throw new Error('当前作业没有题目数据，无法进行批改');
            }

            const questions = {
              question_list: currentHomeworkQuestions.map(q => ({
                stems: q.content || q.stem || '',
                answer: q.answer || ''
              }))
            };

            console.log('构建的questions参数:', questions);
            console.log('questions.question_list:', questions.question_list);

            // 验证questions参数格式
            if (!questions.question_list || questions.question_list.length === 0) {
              throw new Error('题目参数格式错误或为空');
            }

            // 验证每个题目是否有必要的字段
            const invalidQuestions = questions.question_list.filter(q => !q.stems || !q.answer);
            if (invalidQuestions.length > 0) {
              console.warn('发现无效题目:', invalidQuestions);
              throw new Error(`发现 ${invalidQuestions.length} 道题目缺少题干或答案`);
            }

            const response = await judgeQuestion(file, questions);

            // 3. 处理判题结果
            if (response && response.result && response.result.length > 0) {
              const judgeResult = response.result[0];
              const results = judgeResult.results || [];

              // 从judgeQuestion结果中提取学生信息
              const studentId = judgeResult.student_num;
              const studentName = judgeResult.student_name;

              if (!studentId) {
                console.warn('judgeQuestion结果中未找到学生ID，跳过此文件');
                this.uploadHistory.unshift({
                  studentName: '未知学生',
                  fileName: file.name,
                  uploadTime: new Date().toLocaleString(),
                  status: 'error',
                  imageUrl: URL.createObjectURL(file),
                  errorMessage: '未识别到学生信息'
                });
                continue; // 跳过当前文件，继续处理下一个
              }

              // 检查results数组是否为空，如果为空表示批改失败
              if (!results || results.length === 0) {
                console.warn(`学生 ${studentName} (ID: ${studentId}) 的作业批改失败，results为空`);
                this.uploadHistory.unshift({
                  studentName: studentName || '未知学生',
                  fileName: file.name,
                  uploadTime: new Date().toLocaleString(),
                  status: 'error',
                  imageUrl: imageUrl,
                  errorMessage: '作业批改失败，未识别到任何题目'
                });
                continue; // 跳过当前文件，继续处理下一个
              }

              console.log(`识别到学生: ${studentName} (ID: ${studentId})，批改成功，识别到 ${results.length} 道题目`);

              // 根据 matched_index 判断哪些题目被识别了
              const recognizedQuestions = [];
              const unrecognizedQuestions = [];

              currentHomeworkQuestions.forEach((question, index) => {
                const matchedResult = results.find(r => r.matched_index === index + 1);

                if (matchedResult) {
                  recognizedQuestions.push({
                    ...question,
                    judge: matchedResult.judge,
                    studentAnswer: matchedResult.student_answer,
                    comment: matchedResult.comment,
                    correctAnswer: matchedResult.answer || question.answer
                  });
                } else {
                  unrecognizedQuestions.push({
                    ...question,
                    judge: 'unrecognized',
                    studentAnswer: '',
                    comment: '题目未被识别',
                    correctAnswer: question.answer
                  });
                }
              });

              // 保存每个题目的判题结果到数据库
              console.log('开始保存每个题目的判题结果，共', results.length, '个结果');

              for (const result of results) {
                try {
                  // 根据 matched_index 找到对应的题目
                  const questionIndex = result.matched_index - 1;
                  const question = currentHomeworkQuestions[questionIndex];

                  if (question) {
                    // 构造单个题目的判题结果数据
                    const correctionData = {
                      homeworkId: this.homeworkId,
                      studentId: studentId,
                      stem: result.stem || question.content || question.stem || '',
                      judge: result.judge === 'right' ? '正确' : (result.judge === 'error' ? '错误' : '未识别'),
                      comment: result.comment || '',
                      point: result.judge === 'right' ? 10 : 0 // 可以根据需要调整分数
                    };

                    console.log(`保存第${result.matched_index}题判题结果:`, correctionData);
                    await saveCorrectionResult(correctionData);
                    console.log(`第${result.matched_index}题判题结果保存成功`);
                  } else {
                    console.warn(`未找到第${result.matched_index}题对应的题目信息`);
                  }
                } catch (error) {
                  console.error(`保存第${result.matched_index}题判题结果失败:`, error);
                }
              }

              console.log('所有题目判题结果保存完成');

              // 添加错题到错题本
              try {
                const wrongQuestions = results.filter(result => result.judge === 'error');
                if (wrongQuestions.length > 0) {
                  console.log(`发现 ${wrongQuestions.length} 道错题，开始添加到错题本`);

                  for (const wrongResult of wrongQuestions) {
                    try {
                      const questionIndex = wrongResult.matched_index - 1;
                      const question = currentHomeworkQuestions[questionIndex];

                      const wrongQuestionData = {
                        student_id: studentId,
                        content: wrongResult.stem || question?.content || question?.stem || '',
                        analysis: wrongResult.comment || '',
                        knowledge: question?.knowledge || '',
                        chapter: '', // 默认为空
                        suboptions: '数学', // 默认为数学
                        answer: question?.answer || '',
                        wrongCount: 1,
                        mastered: false
                      };

                      console.log(`添加错题到错题本:`, wrongQuestionData);
                      await addWrongQuestion(wrongQuestionData);
                      console.log(`第${wrongResult.matched_index}题已添加到错题本`);
                    } catch (error) {
                      console.error(`添加第${wrongResult.matched_index}题到错题本失败:`, error);
                    }
                  }

                  this.$message.success(`已将 ${wrongQuestions.length} 道错题加入错题本`);
                } else {
                  console.log('没有错题需要添加到错题本');
                }
              } catch (error) {
                console.error('添加错题到错题本失败:', error);
                this.$message.warning('部分错题添加失败，请检查错题本');
              }

              // 创建学生提交记录
              const submissionResponse = await addStudentSubmission({
                homeworkId: this.homeworkId,
                studentId: studentId, // 使用从judgeQuestion结果中提取的学生ID
                submitted: true,
                imageUrl: imageUrl,
                status: '已批改' // 直接在创建时就设置为已批改
              });

              if (submissionResponse && submissionResponse.id) {
                console.log(`学生提交记录创建成功:`, submissionResponse);
              }

              // 添加到总结果列表
              const resultItem = {
                imageUrl: imageUrl,
                fileName: file.name,
                recognizedQuestions: recognizedQuestions,
                unrecognizedQuestions: unrecognizedQuestions,
                totalQuestions: currentHomeworkQuestions.length,
                recognizedCount: recognizedQuestions.length,
                unrecognizedCount: unrecognizedQuestions.length,
                studentId: studentId
              };
              allCorrectionResults.push(resultItem);
              console.log('添加批改结果到总列表:', resultItem);

              // 保存到历史记录
              this.uploadHistory.unshift({
                studentName: studentName || '未知学生',
                fileName: file.name,
                uploadTime: new Date().toLocaleString(),
                status: 'success',
                imageUrl: imageUrl,
                judgeResult: response // 保存原始的judgeQuestion响应
              });

              this.$message.success(`判题完成！识别了 ${recognizedQuestions.length} 道题目，${unrecognizedQuestions.length} 道题目未识别`);

              // 立即更新提交状态
              try {
                // 只要judgeQuestion不返回空值就一律写入已批改
                const status = '已批改';
                console.log(`准备更新状态为${status}，参数:`, this.homeworkId, studentId);
                console.log(`处理结果数量: ${results.length}, 识别题目数: ${recognizedQuestions.length}, 未识别题目数: ${unrecognizedQuestions.length}`);

                const updateResponse = await updateSubmissionStatus(this.homeworkId, studentId, status);
                console.log(`学生 ${studentId} 提交状态更新响应:`, updateResponse);
                console.log(`学生 ${studentId} 提交状态更新为${status}`);
              } catch (error) {
                console.error(`学生 ${studentId} 提交状态更新失败:`, error);
              }
            } else {
              throw new Error('判题结果格式错误');
            }
          } catch (error) {
            console.error(`处理文件 ${file.name} 时出错:`, error);

            this.uploadHistory.unshift({
              studentName: '未知学生',
              fileName: file.name,
              uploadTime: new Date().toLocaleString(),
              status: 'error',
              imageUrl: URL.createObjectURL(file),
              errorMessage: error.message || '处理失败'
            });
          }
        }

        this.$message.success(`成功处理 ${files.length} 个文件`);
        this.resetBatchUpload();
      } catch (error) {
        console.error('批量上传失败', error);
        this.$message.error('批量上传失败，请重试');
      } finally {
        this.uploading = false;
      }
    },

    // 重置批量上传
    resetBatchUpload() {
      this.batchFileList = [];
    },

    // 预览图片
    previewImage(item) {
      this.previewImageUrl = item.imageUrl;
      this.previewDialogVisible = true;
    },

    // 查看批改结果
    viewJudgeResult(item) {
      console.log('查看批改结果，数据结构:', item.judgeResult);
      this.currentJudgeResult = item.judgeResult;
      this.judgeResultDialogVisible = true;
    },

    // 批改结果弹窗相关方法
    // 获取总体状态类型
    getOverallStatusType(results) {
      if (!results || results.length === 0) {
        return 'danger';
      }
      const correctCount = this.getCorrectCount(results);
      const totalCount = results.length;
      const accuracy = (correctCount / totalCount) * 100;

      if (accuracy >= 80) {
        return 'success';
      } else if (accuracy >= 60) {
        return 'warning';
      } else {
        return 'danger';
      }
    },

    // 获取总体状态文本
    getOverallStatusText(results) {
      if (!results || results.length === 0) {
        return '批改失败';
      }
      const correctCount = this.getCorrectCount(results);
      const totalCount = results.length;
      const accuracy = (correctCount / totalCount) * 100;

      if (accuracy >= 80) {
        return '批改成功';
      } else if (accuracy >= 60) {
        return '部分正确';
      } else {
        return '需要改进';
      }
    },

    // 获取正确题目数量
    getCorrectCount(results) {
      if (!results) return 0;
      return results.filter(result => result.judge === 'right').length;
    },

    // 获取错误题目数量
    getErrorCount(results) {
      if (!results) return 0;
      return results.filter(result => result.judge === 'error').length;
    },

    // 获取正确率
    getAccuracy(results) {
      if (!results || results.length === 0) return 0;
      const correctCount = this.getCorrectCount(results);
      return Math.round((correctCount / results.length) * 100);
    },

    // 获取表格行样式类名
    getRowClassName({ row, rowIndex }) {
      if (row.judge === 'right') {
        return 'success-row';
      } else if (row.judge === 'error') {
        return 'error-row';
      }
      return '';
    },

    // 对结果按matched_index排序
    getSortedResults(results) {
      if (!results || results.length === 0) return [];
      return [...results].sort((a, b) => (a.matched_index || 0) - (b.matched_index || 0));
    },

    // 渲染LaTeX数学公式
    renderLatex(text) {
      if (!text) return '';

      // 简单的LaTeX渲染处理
      let rendered = text
        // 处理换行
        .replace(/\n/g, '<br>')
        // 处理特殊字符（先处理这些，避免被其他规则影响）
        .replace(/\\leq/g, '≤')
        .replace(/\\geq/g, '≥')
        .replace(/\\neq/g, '≠')
        .replace(/\\infty/g, '∞')
        .replace(/\\cup/g, '∪')
        .replace(/\\cap/g, '∩')
        .replace(/\\in/g, '∈')
        .replace(/\\notin/g, '∉')
        .replace(/\\subset/g, '⊂')
        .replace(/\\supset/g, '⊃')
        .replace(/\\subseteq/g, '⊆')
        .replace(/\\supseteq/g, '⊇')
        .replace(/\\emptyset/g, '∅')
        .replace(/\\mathbb\{R\}/g, 'ℝ')
        .replace(/\\mathbb\{N\}/g, 'ℕ')
        .replace(/\\mathbb\{Z\}/g, 'ℤ')
        .replace(/\\mathbb\{Q\}/g, 'ℚ')
        .replace(/\\mathbb\{C\}/g, 'ℂ')
        .replace(/\\alpha/g, 'α')
        .replace(/\\beta/g, 'β')
        .replace(/\\gamma/g, 'γ')
        .replace(/\\delta/g, 'δ')
        .replace(/\\epsilon/g, 'ε')
        .replace(/\\theta/g, 'θ')
        .replace(/\\lambda/g, 'λ')
        .replace(/\\mu/g, 'μ')
        .replace(/\\pi/g, 'π')
        .replace(/\\sigma/g, 'σ')
        .replace(/\\tau/g, 'τ')
        .replace(/\\phi/g, 'φ')
        .replace(/\\omega/g, 'ω')
        // 处理分数（在特殊字符之后处理）
        .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="fraction"><sup>$1</sup>/<sub>$2</sub></span>')
        // 处理根号
        .replace(/\\sqrt\{([^}]+)\}/g, '√<span class="radicand">$1</span>')
        // 处理上标
        .replace(/\^(\d+)/g, '<sup>$1</sup>')
        .replace(/\^([a-zA-Z])/g, '<sup>$1</sup>')
        // 处理下标
        .replace(/_(\d+)/g, '<sub>$1</sub>')
        .replace(/_([a-zA-Z])/g, '<sub>$1</sub>')
        // 处理大括号（集合表示）
        .replace(/\\\{/g, '{')
        .replace(/\\\}/g, '}')
        // 处理分段函数
        .replace(/\\begin\{cases\}([^}]+)\\end\{cases\}/g, (match, content) => {
          const cases = content.split('\\\\').map(caseItem => {
            const parts = caseItem.split('&');
            if (parts.length === 2) {
              return `<div class="case-item">${parts[0].trim()} &nbsp;&nbsp; ${parts[1].trim()}</div>`;
            }
            return `<div class="case-item">${caseItem.trim()}</div>`;
          }).join('');
          return `<div class="cases">${cases}</div>`;
        })
        // 处理绝对值
        .replace(/\\left\|([^|]+)\\right\|/g, '|$1|')
        // 处理行内数学公式 \( ... \)（最后处理，避免影响其他规则）
        .replace(/\\\(([^)]+)\\\)/g, '<span class="math-inline">$1</span>')
        // 处理块级数学公式 \[ ... \]
        .replace(/\\\[([^\]]+)\\\]/g, '<div class="math-block">$1</div>')
        // 处理美元符号数学公式 $ ... $
        .replace(/\$([^$]+)\$/g, '<span class="math-inline">$1</span>')
        // 处理双美元符号数学公式 $$ ... $$
        .replace(/\$\$([^$]+)\$\$/g, '<div class="math-block">$1</div>')
        // 处理换行符
        .replace(/\\\\/g, '<br>')
        // 处理对齐符号
        .replace(/&/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
        // 处理空格
        .replace(/\\,/g, '&nbsp;')
        .replace(/\\;/g, '&nbsp;&nbsp;')
        .replace(/\\quad/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
        .replace(/\\qquad/g, '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;');

      return rendered;
    }
  }
}
</script>

<style scoped>
.course-class-homework-upload {
  padding: 20px;
}

.info-section {
  margin-bottom: 20px;
}

.batch-upload-container {
  margin-top: 20px;
}

.batch-upload-section {
  margin-top: 20px;
}

.student-select, .image-upload, .batch-image-upload {
  margin-bottom: 20px;
}

h4 {
  margin-bottom: 15px;
  color: #606266;
  font-size: 16px;
}

.upload-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.upload-history {
  margin-top: 40px;
}

.batch-info {
  margin-bottom: 20px;
}

.image-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.preview-image {
  max-width: 100%;
  max-height: 500px;
}

.no-image {
  color: #909399;
  font-size: 14px;
}

.judge-result-container {
  padding: 10px 0;
}

.student-result {
  margin-bottom: 30px;
}

.student-result .el-divider {
  margin: 20px 0;
}

.no-result {
  color: #909399;
  font-size: 14px;
  text-align: center;
  padding: 20px;
}

.homework-info-display {
  margin-top: 15px;
}

.homework-alert {
  border-radius: 8px;
}

.homework-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.homework-header i {
  font-size: 20px;
  color: #409EFF;
  margin-right: 8px;
}

.homework-header h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.homework-card {
  border-radius: 8px;
  border: none;
}

.homework-content {
  padding: 5px 0;
}

.homework-name, .homework-id {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.homework-name .label, .homework-id .label {
  font-weight: 500;
  color: #606266;
  min-width: 80px;
  margin-right: 10px;
}

.homework-name .value {
  font-weight: 600;
  color: #303133;
  font-size: 16px;
}

.homework-id .el-tag {
  font-weight: 500;
}

/* 批改结果弹窗样式 */
.judge-result-container {
  padding: 10px 0;
}

.student-result {
  margin-bottom: 30px;
}

.student-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.student-info h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.overall-status .el-tag {
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 20px;
}

.statistics {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.stat-item {
  text-align: center;
  padding: 15px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: transform 0.2s;
}

.stat-item:hover {
  transform: translateY(-2px);
}

.stat-item.correct {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.stat-item.error {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #495057;
  margin-bottom: 5px;
}

.stat-item.correct .stat-number {
  color: #155724;
}

.stat-item.error .stat-number {
  color: #721c24;
}

.stat-label {
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}

.questions-table {
  margin-top: 20px;
}

.question-content, .answer-content {
  line-height: 1.6;
  word-break: break-word;
}

.comment-content {
  line-height: 1.5;
  color: #606266;
  font-size: 13px;
}

/* 数学公式样式 */
.math-inline {
  font-family: 'Times New Roman', serif;
  font-style: italic;
  background: #f0f0f0;
  padding: 2px 4px;
  border-radius: 3px;
  margin: 0 2px;
  display: inline-block;
}

.math-block {
  font-family: 'Times New Roman', serif;
  text-align: center;
  margin: 10px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 5px;
  border-left: 4px solid #007bff;
}

.fraction {
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  margin: 0 2px;
}

.fraction sup {
  display: block;
  border-bottom: 1px solid #000;
  padding-bottom: 1px;
  font-size: 0.8em;
}

.fraction sub {
  display: block;
  padding-top: 1px;
  font-size: 0.8em;
}

.radicand {
  text-decoration: overline;
  padding: 0 2px;
}

.cases {
  text-align: left;
  margin: 5px 0;
  padding: 5px;
  background: #f8f9fa;
  border-radius: 3px;
  border: 1px solid #dee2e6;
}

.case-item {
  margin: 2px 0;
  padding: 2px 0;
}

/* 数学符号样式 */
sup {
  font-size: 0.7em;
  vertical-align: super;
  line-height: 0;
}

sub {
  font-size: 0.7em;
  vertical-align: sub;
  line-height: 0;
}

/* 集合符号样式 */
.question-content, .answer-content {
  font-family: 'Times New Roman', serif;
  line-height: 1.6;
  word-break: break-word;
}

.question-content .math-inline,
.answer-content .math-inline {
  font-family: 'Times New Roman', serif;
  font-style: italic;
  background: #e8f4fd;
  padding: 1px 3px;
  border-radius: 2px;
  margin: 0 1px;
  border: 1px solid #b3d9ff;
}

/* 表格行样式 */
.el-table .success-row {
  background-color: #f0f9ff;
}

.el-table .error-row {
  background-color: #fef2f2;
}

.el-table .success-row:hover > td {
  background-color: #e0f2fe !important;
}

.el-table .error-row:hover > td {
  background-color: #fce7e7 !important;
}

/* 学生列表相关样式 */
.student-list-warning {
  margin-top: 10px;
}

.student-list-info {
  margin-top: 5px;
  display: flex;
  align-items: center;
}

/* 加载状态样式 */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 20px 0;
}

.loading-content {
  text-align: center;
  color: #666;
}

.loading-content i {
  font-size: 24px;
  color: #409EFF;
  margin-bottom: 10px;
  display: block;
  animation: rotating 2s linear infinite;
}

.loading-content p {
  margin: 0;
  font-size: 14px;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 确保组件可以操作 */
.course-class-homework-upload {
  pointer-events: auto !important;
  opacity: 1 !important;
}

.course-class-homework-upload * {
  pointer-events: auto !important;
}

/* 确保上传组件可以操作 */
.upload-demo {
  pointer-events: auto !important;
}

.upload-demo * {
  pointer-events: auto !important;
}

/* 确保按钮可以点击 */
.el-button {
  pointer-events: auto !important;
  cursor: pointer !important;
}

/* 确保选择框可以操作 */
.el-select {
  pointer-events: auto !important;
}

.el-select * {
  pointer-events: auto !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .student-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .statistics .el-col {
    margin-bottom: 10px;
  }

  .questions-table .el-table {
    font-size: 12px;
  }
}
</style>
